import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const ADMIN_TOKEN_KEY = 'ADMIN_TOKEN';

const FORM_VIEWS = {
  AUTH: 'auth',
  OTP: 'otp',
  FORGOT: 'forgot',
  RESET: 'reset',
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formView, setFormView] = useState(FORM_VIEWS.AUTH);
  const [otpCode, setOtpCode] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetEmail, setResetEmail] = useState('');

  const toggleMode = () => {
    setMode((prev) => (prev === 'login' ? 'register' : 'login'));
    setError('');
    setInfoMessage('');
    setFormView(FORM_VIEWS.AUTH);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    setIsSubmitting(true);

    try {
      const endpoint = mode === 'register' ? '/admin/register' : '/admin/login';
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Unable to authenticate');
      }

      if (data.requiresOtp) {
        setFormView(FORM_VIEWS.OTP);
        setError('');
        
        // If OTP is provided in response (dev mode only), show it in UI only (never log to console)
        if (data.otp) {
          setInfoMessage(`Email delivery failed. Use this OTP code: ${data.otp}`);
        } else if (data.warning) {
          setInfoMessage(data.warning);
        }
      } else if (data.token) {
        localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
        navigate('/admindashboard');
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (authError) {
      console.error('Login error:', authError);
      setError(authError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    setIsSubmitting(true);

    try {
      // Normalize OTP input (trim whitespace)
      const normalizedOtp = otpCode.trim();
      
      if (!normalizedOtp || normalizedOtp.length !== 6) {
        setError('Please enter a valid 6-digit OTP code');
        setIsSubmitting(false);
        return;
      }

      console.log(`Verifying OTP for email: ${email}`);
      
      const response = await fetch(`${API_BASE_URL}/admin/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp: normalizedOtp }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('OTP verification failed:', data.message);
        throw new Error(data.message || 'Failed to verify OTP');
      }

      console.log('OTP verified successfully');
      localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      setFormView(FORM_VIEWS.AUTH);
      setOtpCode('');
      navigate('/admindashboard');
    } catch (otpError) {
      console.error('OTP verification error:', otpError);
      setError(otpError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Unable to process request');
      }

      setResetEmail(email);
      setFormView(FORM_VIEWS.RESET);
      setInfoMessage('Reset code sent. Check your email and enter the code below.');
      setResetCode('');
      setResetPassword('');
      setResetPasswordConfirm('');
    } catch (forgotError) {
      setError(forgotError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');

    if (resetPassword !== resetPasswordConfirm) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: resetEmail || email,
          code: resetCode,
          newPassword: resetPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Unable to reset password');
      }

      setInfoMessage('Password reset successful. Please log in with your new password.');
      setFormView(FORM_VIEWS.AUTH);
      setMode('login');
      setPassword('');
      setResetPassword('');
      setResetPasswordConfirm('');
      setResetCode('');
    } catch (resetError) {
      setError(resetError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTitle = () => {
    if (formView === FORM_VIEWS.OTP) return 'Verify OTP Code';
    if (formView === FORM_VIEWS.FORGOT) return 'Forgot Password';
    if (formView === FORM_VIEWS.RESET) return 'Reset Password';
    return mode === 'register' ? 'Create Admin Account' : 'Log in to your Account';
  };

  const renderSubtitle = () => {
    if (formView === FORM_VIEWS.OTP) return `Check your email (${email}) for the OTP code`;
    if (formView === FORM_VIEWS.FORGOT) return 'Enter your admin email to receive a reset code.';
    if (formView === FORM_VIEWS.RESET) {
      const targetEmail = resetEmail || email;
      return `Enter the reset code sent to ${targetEmail || 'your email'} and choose a new password.`;
    }
    return mode === 'register'
      ? 'Enter your email and password to create an admin account'
      : 'Please enter your details.';
  };

  const resetToLogin = () => {
    setFormView(FORM_VIEWS.AUTH);
    setMode('login');
    setError('');
    setInfoMessage('');
  };

  return (
    <div
      className="min-h-screen gap-[209px] text-white flex items-center justify-center p-6 select-none cursor-default"
      style={{
        background: 'radial-gradient(circle at 18% 50%, #151419 2%, #151419 7%, #000000 100%)',
      }}
    >
      
      {/* Main content wrapper (2 columns) */}
      <div className="max-w-7xl w-full grid grid-cols-1 md:grid-cols-2 gap-20 md:gap-[425px] items-center select-none cursor-default">

        {/* === LEFT COLUMN: Logo === */}
        <div className="relative flex items-center justify-center select-none cursor-default w-[500px] h-[500px] mx-auto">
          <div 
            className="absolute inset-0 rounded-full blur-2xl opacity-80"
            style={{ 
              background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(21,20,25,0.1) 50%, rgba(0,0,0,0) 100%)'
            }}
          ></div>
          <img 
            src={require('../images/circular_logo.svg').default} 
            alt="The Adox Intelligence Virtual Logo" 
            className="select-none cursor-default relative z-10" 
            style={{ width: '500px', height: '500px' }}
          />
        </div>

        {/* === RIGHT COLUMN: Login Form === */}
        <div 
          className="mx-auto select-none cursor-default bg-white rounded-lg p-8"
          style={{
            width: '509px',
            height: '560px',
            boxShadow: '0 0 40px rgba(255, 255, 255, 0.1), 0 0 80px rgba(255, 255, 255, 0.1), 0 0 120px rgba(255, 255, 255, 0.1)'
          }}
        >
          
          <h2 className="text-[30px] font-bold mb-2 mt-10 text-center select-none cursor-default text-black h-12"> 
            {renderTitle()}
          </h2>
          <p className="text-[16px] mb-8 text-center select-none cursor-default text-black">
            {renderSubtitle()}
          </p>

          {infoMessage && (
            <p className="text-center text-sm text-green-600 mb-4">{infoMessage}</p>
          )}

          {formView === FORM_VIEWS.AUTH && (
          <form onSubmit={handleSubmit} className="space-y-5 select-none cursor-default">
            {/* ... (Form fields: Admin, Password, Button) ... */}
            
            {/* Email Field */}
            <div className="select-none cursor-default flex flex-col items-center">
              <label htmlFor="email" className="block text-sm font-medium text-[#0A1633] select-none cursor-default w-[360px] text-left">{ /* previously text-gray-300 */}
                Email
              </label>
              <div className="mt-1">
                <input
                  type="email"
                  name="email"
                  id="email"
                  autoComplete="username"
                  placeholder='admin@email.com'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="px-4 py-3 rounded-xl bg-white border border-[#D7D9E2] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E68A40] focus:border-[#E68A40] focus:cursor-text cursor-default transition-colors mx-auto block"
                  style={{ width: '360px' }}
                /> { /* previously text-gray-300 */}
              </div>
            </div>

            {/* Password Field */}
            <div className="select-none cursor-default flex flex-col items-center ">
              <label htmlFor="password" className=" block text-sm font-medium select-none cursor-default text-[#0A1633] w-[360px] text-left"> { /* previously text-gray-300 */}
                Password
              </label>
              <div className="mt-1">
                <input
                  type="password"
                  name="password"
                  id="password"
                  autoComplete="current-password"
                  placeholder='Password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="px-4 py-3  rounded-xl bg-white border border-[#D7D9E2] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E68A40] focus:border-[#E68A40] focus:cursor-text cursor-default transition-colors mx-auto block"
                  style={{ width: '360px' }}
                />
              </div>
            </div>

            {/* Login Button */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`py-3 px-4 mt-4 rounded-lg shadow-sm text-lg font-semibold text-white bg-[#E68A40] hover:bg-opacity-90 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E68A40] cursor-pointer select-none w-[360px] ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Please wait...' : mode === 'register' ? 'Create Admin Account' : 'Login'}
              </button>
            </div>

            {error && (
              <p className="text-center text-sm text-red-500 mt-4 select-none cursor-default">{error}</p>
            )}

            {mode === 'login' && (
              <div className="text-center mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setFormView(FORM_VIEWS.FORGOT);
                    setError('');
                    setInfoMessage('');
                  }}
                  className="text-[#E68A40] hover:text-[#c06829] text-sm font-semibold"
                >
                  Forgot password?
                </button>
              </div>
            )}

            <div className="text-center mt-6 mb-10">
              <button
                type="button"
                onClick={toggleMode}
                className="text-[#E68A40] hover:text-[#c06829] text-sm font-semibold"
              >
                {mode === 'register'
                  ? 'Already have access? Sign in'
                  : 'First time setup? Create admin account'}
              </button>
            </div>
          </form>
          )}

          {formView === FORM_VIEWS.OTP && (
            <form onSubmit={handleOtpVerify} className="space-y-5 select-none cursor-default">
              <div className="select-none cursor-default flex flex-col items-center">
                <label htmlFor="otp" className="block text-sm font-medium text-[#0A1633] select-none cursor-default w-[360px] text-left">
                  Enter OTP sent to {email}
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="otp"
                    id="otp"
                    placeholder="6-digit code"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    className="px-4 py-3 rounded-xl bg-white border border-[#D7D9E2] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E68A40] focus:border-[#E68A40] focus:cursor-text cursor-default transition-colors mx-auto block"
                    style={{ width: '360px' }}
                  />
                </div>
              </div>
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`py-3 px-4 mt-4 rounded-lg shadow-sm text-lg font-semibold text-white bg-[#E68A40] hover:bg-opacity-90 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E68A40] cursor-pointer select-none w-[360px] ${
                    isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Validating...' : 'Verify OTP'}
                </button>
              </div>
              {error && (
                <p className="text-center text-sm text-red-500 mt-4 select-none cursor-default">{error}</p>
              )}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setFormView(FORM_VIEWS.AUTH);
                    setOtpCode('');
                    setError('');
                  }}
                  className="text-[#E68A40] hover:text-[#c06829] text-sm font-semibold"
                >
                  Back to {mode === 'register' ? 'registration' : 'login'}
                </button>
              </div>
            </form>
          )}

          {formView === FORM_VIEWS.FORGOT && (
            <form onSubmit={handleForgotSubmit} className="space-y-5 select-none cursor-default">
              <div className="select-none cursor-default flex flex-col items-center">
                <label htmlFor="forgot-email" className="block text-sm font-medium text-[#0A1633] select-none cursor-default w-[360px] text-left">
                  Admin Email
                </label>
                <div className="mt-1">
                  <input
                    type="email"
                    name="forgot-email"
                    id="forgot-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="px-4 py-3 rounded-xl bg-white border border-[#D7D9E2] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E68A40] focus:border-[#E68A40] cursor-text transition-colors mx-auto block"
                    style={{ width: '360px' }}
                  />
                </div>
              </div>
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`py-3 px-4 mt-4 rounded-lg shadow-sm text-lg font-semibold text-white bg-[#E68A40] hover:bg-opacity-90 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E68A40] cursor-pointer select-none w-[360px] ${
                    isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Reset Code'}
                </button>
              </div>
              {error && (
                <p className="text-center text-sm text-red-500 mt-4 select-none cursor-default">{error}</p>
              )}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={resetToLogin}
                  className="text-[#E68A40] hover:text-[#c06829] text-sm font-semibold"
                >
                  Back to login
                </button>
              </div>
            </form>
          )}

          {formView === FORM_VIEWS.RESET && (
            <form onSubmit={handleResetSubmit} className="space-y-5 select-none cursor-default">
              <div className="select-none cursor-default flex flex-col items-center">
                <label htmlFor="reset-code" className="block text-sm font-medium text-[#0A1633] select-none cursor-default w-[360px] text-left">
                  Reset Code
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="reset-code"
                    id="reset-code"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    placeholder="6-digit code"
                    className="px-4 py-3 rounded-xl bg-white border border-[#D7D9E2] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E68A40] focus:border-[#E68A40] cursor-text transition-colors mx-auto block"
                    style={{ width: '360px' }}
                  />
                </div>
              </div>

              <div className="select-none cursor-default flex flex-col items-center">
                <label htmlFor="new-password" className="block text-sm font-medium text-[#0A1633] select-none cursor-default w-[360px] text-left">
                  New Password
                </label>
                <div className="mt-1">
                  <input
                    type="password"
                    name="new-password"
                    id="new-password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="New password"
                    className="px-4 py-3 rounded-xl bg-white border border-[#D7D9E2] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E68A40] focus:border-[#E68A40] cursor-text transition-colors mx-auto block"
                    style={{ width: '360px' }}
                  />
                </div>
              </div>

              <div className="select-none cursor-default flex flex-col items-center">
                <label htmlFor="confirm-password" className="block text-sm font-medium text-[#0A1633] select-none cursor-default w-[360px] text-left">
                  Confirm Password
                </label>
                <div className="mt-1">
                  <input
                    type="password"
                    name="confirm-password"
                    id="confirm-password"
                    value={resetPasswordConfirm}
                    onChange={(e) => setResetPasswordConfirm(e.target.value)}
                    placeholder="Confirm password"
                    className="px-4 py-3 rounded-xl bg-white border border-[#D7D9E2] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E68A40] focus:border-[#E68A40] cursor-text transition-colors mx-auto block"
                    style={{ width: '360px' }}
                  />
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`py-3 px-4 mt-4 rounded-lg shadow-sm text-lg font-semibold text-white bg-[#E68A40] hover:bg-opacity-90 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E68A40] cursor-pointer select-none w-[360px] ${
                    isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Updating...' : 'Reset Password'}
                </button>
              </div>
              {error && (
                <p className="text-center text-sm text-red-500 mt-4 select-none cursor-default">{error}</p>
              )}
              <div className="text-center mt-4 space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setFormView(FORM_VIEWS.FORGOT);
                    setInfoMessage('');
                    setError('');
                  }}
                  className="text-[#E68A40] hover:text-[#c06829] text-sm font-semibold"
                >
                  Didn’t get the code?
                </button>
                <button
                  type="button"
                  onClick={resetToLogin}
                  className="text-[#E68A40] hover:text-[#c06829] text-sm font-semibold"
                >
                  Back to login
                </button>
              </div>
            </form>
          )}
        </div>

      </div>
    </div>
  );
};

export default LoginPage;